export enum PollState {
  Awaiting = 'awaiting',
  Loading = 'loading',
  Ready = 'ready',
  Refreshing = 'refreshing',
  Error = 'error',
}

export function isSettledOrActivePoll(state: PollState | null): boolean {
  return (
    state === PollState.Awaiting ||
    state === PollState.Loading ||
    state === PollState.Refreshing ||
    state === PollState.Ready ||
    state === PollState.Error
  )
}

/**
 * shared package accounting and overview-readiness gate for every poll cache.
 */
export class PollProgress {
  polled = false
  private overviewReady = false
  private failure?: unknown
  private finalInitialPollPackage?: number | null
  private receivedInitialPollPackages: boolean[] = []

  markOverviewReady() {
    this.overviewReady = true
  }

  markFailed(reason?: unknown) {
    this.failure = reason ?? new Error('Poll failed.')
  }

  hasFailed(): boolean {
    return this.failure !== undefined
  }

  setFinalInitialPollPackage(finalInitialPollPackage: number) {
    if (!this.polled) {
      this.finalInitialPollPackage = finalInitialPollPackage
    }
  }

  setReceivedInitialPollPackage(receivedInitialPollPackage: number) {
    if (!this.polled) {
      this.receivedInitialPollPackages[receivedInitialPollPackage - 1] = true

      if (this.finalInitialPollPackage) {
        let ok = true
        for (let i = 0; i < this.finalInitialPollPackage; i++) {
          if (!this.receivedInitialPollPackages[i]) {
            ok = false
            break
          }
        }
        if (ok) {
          this.polled = true
          this.receivedInitialPollPackages = []
          this.finalInitialPollPackage = null
        }
      }
    }
  }

  async hasOverviewData(): Promise<void> {
    if (this.overviewReady) {
      return
    }
    if (this.failure) {
      throw this.failure
    }

    return new Promise<void>((resolve, reject) => {
      const check = () => {
        if (this.overviewReady) {
          resolve()
          return
        }
        if (this.failure) {
          reject(this.failure)
          return
        }
        setTimeout(check, 0)
      }
      check()
    })
  }

  async hasPolled(): Promise<void> {
    return new Promise<void>(resolve => {
      const check = () => {
        if (this.polled) {
          resolve()
          return
        }
        setTimeout(check, 0)
      }
      check()
    })
  }
}

/**
 * serializes async poll-package persistence so later worker messages cannot
 * mark readiness while an earlier package is still writing.
 */
export class PollWriteQueue {
  private queue: Promise<void> = Promise.resolve()

  enqueue<T extends PollProgress>(
    getActive: () => T | undefined,
    cache: T,
    work: () => Promise<void>,
    onError: (error: unknown) => void
  ): void {
    this.queue = this.queue
      .then(async () => {
        if (getActive() !== cache || cache.hasFailed()) {
          return
        }
        await work()
      })
      .catch(error => {
        cache.markFailed(error)
        onError(error)
      })
  }

  async drain(): Promise<void> {
    await this.queue.catch(() => undefined)
    this.queue = Promise.resolve()
  }
}
